import { useEffect } from "react";
import { stagger, useAnimate, useInView } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1] as const;

// Page-load timeline, in seconds after the page first paints:
//   0.00 name + title (CSS, Hero, 0.6s)   0.15 intro (CSS, Hero, done at 0.75)
//   0.75 Experience   1.00 Skills   1.25 next section …   1.50 the dock
// Nothing below the hero starts until the hero has finished.
const FIRST_SECTION_AT = 0.75;
const STEP = 0.25;
export const DOCK_AT = 1.5;

// Seconds from now until `t` seconds after the hero's fade began. Anchored to the hero's
// CSS animation (not first paint): in production the prerendered hero starts before JS loads,
// in dev it starts when React mounts it. Not started yet (or reduced motion) = now.
function heroStart() {
  const anim = document.querySelector("#hero .animate-hero-in")?.getAnimations()[0];
  const start = anim?.startTime;
  return typeof start === "number" ? start : performance.now();
}

export function timelineDelay(t: number) {
  return Math.max(0, (heroStart() + t * 1000 - performance.now()) / 1000);
}

// On first load every section already on screen gets a fixed slot from its position on the
// page (`order`), so Experience is always step 0 and Skills step 1, whatever order the browser
// reports them in. Sections reached later by scrolling just fade in, staggered if several
// arrive together.
let loadWindowOpen = true;
const closeLoadWindow = () => (loadWindowOpen = false);
if (typeof window !== "undefined") {
  setTimeout(closeLoadWindow, 2500);
  // The visitor started scrolling: don't hold newly visible sections back for the timeline
  for (const e of ["wheel", "touchmove", "keydown"]) window.addEventListener(e, closeLoadWindow, { once: true, passive: true });
}
let batchIndex = 0;
let batchAt = -Infinity;

function nextDelay(order: number) {
  if (loadWindowOpen) {
    // Late JS still keeps the order: never less than `order` steps apart
    return Math.max(timelineDelay(FIRST_SECTION_AT + order * STEP), order * STEP);
  }
  const now = performance.now();
  if (now - batchAt > 300) batchIndex = 0;
  batchAt = now;
  return batchIndex++ * 0.12;
}

// Fades a section up (with a slight blur) the first time it scrolls into view.
// The hidden state is a plain inline style and the fade runs imperatively: the page
// transition's <AnimatePresence initial={false}> would make a declarative `initial` a no-op.
const HIDDEN = { opacity: 0, transform: "translateY(20px)", filter: "blur(6px)" };

export function Reveal({ children, order }: { children: React.ReactNode; order: number }) {
  const [scope, animate] = useAnimate<HTMLDivElement>();
  const inView = useInView(scope, { once: true, margin: "0px 0px -8% 0px" });

  useEffect(() => {
    if (!inView) return;
    animate(
      scope.current,
      { opacity: 1, transform: "translateY(0px)", filter: "blur(0px)" },
      { duration: 0.7, delay: nextDelay(order), ease: EASE }
    );
  }, [inView, animate, scope, order]);

  return (
    <div ref={scope} style={HIDDEN}>
      {children}
    </div>
  );
}

// Top-to-bottom entrance for page content (Blogs, Projects, a post): every [data-stagger]
// element inside the returned ref fades up in DOM order. Their hidden start is CSS (index.css);
// run again via `deps` when new content arrives (e.g. posts loaded) and only the new items animate.
export function useStaggerIn<T extends HTMLElement>(deps: unknown[] = []) {
  const [scope, animate] = useAnimate<T>();
  useEffect(() => {
    const items = [...(scope.current?.querySelectorAll<HTMLElement>("[data-stagger]") ?? [])].filter(
      (el) => el.style.opacity !== "1"
    );
    if (!items.length) return;
    animate(
      items,
      { opacity: [0, 1], transform: ["translateY(12px)", "translateY(0px)"] },
      { duration: 0.4, delay: stagger(0.05), ease: EASE }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return scope;
}
